import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { useEffect, useLayoutEffect, useRef } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/hu';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getDisplayDate } from '../../../../utils/dateUtils';
import useGetBooking from '../../api/useGetBooking';
import SupplyDetails from '../SupplyDetails';
import BookingDecisionButtons from '../BookingDecisionButtons';
import PersonAvatar from '../PersonAvatar';
import { getStatusConfig, isClosedStatus } from '../../utils/bookingStatus';
import { timeToMinutes } from '../../utils/scheduleUtils';
import { useSnackbar } from '../../../../contexts/SnackBarContext';
import { useTenant } from '../../../../contexts/TenantContext';
import useDeleteBooking, { DeleteOutcome } from '../../../../api/useDeleteBooking';
import { useQueryClient } from '@tanstack/react-query';

const TITLE_ID = 'booking-details-title';

/**
 * A line that shrinks rather than being cut. The date and time of a booking must always be read in
 * full, and on a phone the two tiles side by side leave them little room: the text steps down from
 * `maxSize` towards `minSize` until it fits, and only if it still does not fit at `minSize` does it
 * wrap.
 */
const FitText = ({ children, maxSize, minSize, sx }) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const element = ref.current;
    const overflows = () => element.scrollWidth > element.clientWidth;

    const fit = () => {
      let size = maxSize;
      element.style.whiteSpace = 'nowrap';
      element.style.fontSize = `${size}px`;
      // Text width grows in step with font size, so one scaled guess lands close; the loop takes
      // it the rest of the way past rounding.
      if (overflows()) {
        size = Math.max(minSize, Math.floor(((maxSize * element.clientWidth) / element.scrollWidth) * 2) / 2);
        element.style.fontSize = `${size}px`;
      }
      while (overflows() && size > minSize) {
        size = Math.max(minSize, size - 0.5);
        element.style.fontSize = `${size}px`;
      }
      if (overflows()) element.style.whiteSpace = 'normal';
    };

    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [children, maxSize, minSize]);

  return (
    <Typography ref={ref} sx={{ ...sx, fontSize: `${maxSize}px`, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
  );
};

const InfoTile = ({ icon, label, value, detail }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start" flex={1} minWidth={0}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '8px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0,0,0,0.04)',
        color: 'text.secondary',
        // On the narrowest phones the icon's room goes to the date and time instead (DECISIONS.md D25).
        '@media (max-width: 359.95px)': { display: 'none' },
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0} flex={1}>
      <Typography sx={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#999' }}>
        {label}
      </Typography>
      <FitText maxSize={15} minSize={10} sx={{ fontWeight: 700, color: '#222', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </FitText>
      {detail && (
        <FitText maxSize={12} minSize={9} sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
          {detail}
        </FitText>
      )}
    </Box>
  </Stack>
);

/**
 * One booking, opened from either board. A sibling of `PendingBookingCard` — the same dark band
 * with the guest in it over a white body — so a booking looks the same wherever the admin meets
 * it, and like the card, it keeps to the dashboard's neutral palette (see `SupplyDetails`).
 *
 * A PENDING booking also carries the approve / reject pair, on a tenant that approves bookings
 * at all — and then no delete (DECISIONS.md D22). The decision itself is not taken here:
 * `onDecision` hands it to the same confirmation modal the pending card uses.
 */
const BookingDetailsModal = (props) => {
  const { show, handleClose, bookingId, reloadSignal = () => null, onDecision } = props;
  const { getBooking, data: bookingData, loading: bookingLoading } = useGetBooking();
  const { deleteBooking: requestDelete, loading } = useDeleteBooking();
  const { isBookingApprovalRequired } = useTenant();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  // What the board shows has changed: reload it, and the pending list beside it — without that,
  // a pending card would remain until the next refetch, though its booking is already gone.
  const reloadBoardAndClose = () => {
    reloadSignal();
    queryClient.invalidateQueries({
      queryKey: ['pendingBookings']
    });
    handleClose();
  };

  // On every opening, not only when the id changes: reopening a booking after approving it must
  // not show it as still pending.
  //
  // The board can be up to five minutes old (DECISIONS.md D17), so the booking clicked may have
  // been cancelled since. Then the card closes instead of spinning on, and the board catches up.
  // An answer that arrives after this opening has ended changes nothing.
  useEffect(() => {
    if (!show || !bookingId || bookingId === "") return undefined;

    let isCurrentOpening = true;
    getBooking(bookingId).then((isGone) => {
      if (isCurrentOpening && isGone) reloadBoardAndClose();
    });

    return () => {
      isCurrentOpening = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, show]);

  // The hook keeps the last booking it fetched. Shown only once this opening's request is back:
  // otherwise a second booking would open as the first, and a just-approved one as still
  // pending — with its approve button live. Not blanked while the card fades out, since nothing
  // is loading then.
  const booking = !bookingLoading && bookingData && String(bookingData.id) === String(bookingId)
    ? bookingData
    : null;

  const status = getStatusConfig(booking?.status);
  const StatusIcon = status.Icon;
  const isBlocked = booking?.status === 'BLOCKED';
  const canDecide = isBookingApprovalRequired && booking?.status === 'PENDING' && Boolean(onDecision);
  // Where rejecting is on offer, deleting is not: a delete mails no one, so the guest would never
  // hear that their request is gone, while a rejection tells them. A PENDING booking with no
  // decision on offer keeps the delete, or nothing on the dashboard could clear it. A booking
  // that has ended is history: it can be looked at, not changed.
  const canDelete = !canDecide && !isClosedStatus(booking?.status);

  const date = booking ? dayjs(booking.date).locale('hu') : null;
  const durationMinutes = booking?.startTime && booking?.endTime
    ? timeToMinutes(booking.endTime) - timeToMinutes(booking.startTime)
    : null;

  const guestName = booking?.name || (isBlocked ? 'Letiltott időszak' : 'Ismeretlen vendég');

  const deleteBooking = async () => {
    if (bookingId && bookingId !== "") {

      const outcome = await requestDelete(bookingId);
      if (outcome === DeleteOutcome.FAILED) return;

      // GONE: the guest cancelled it while the card was open, and the snackbar has said so.
      if (outcome === DeleteOutcome.DELETED) {
        showSnackbar(isBlocked ? "Sikeres feloldás" : "Sikeres törlés", "success");
      }
      reloadBoardAndClose();
    }
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby={TITLE_ID}
      PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', m: 2, width: 'calc(100% - 32px)' } }}
    >
      {/* Dark header band — the guest, and what state the booking is in */}
      <Box
        sx={{
          bgcolor: '#1a1a1a',
          color: '#fff',
          px: 3,
          pt: 2,
          pb: 2.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
            Foglalás részletei
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="Bezárás"
            size="small"
            sx={{ color: 'rgba(255,255,255,0.6)', mr: -1, zIndex: 1, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" mt={1.5} position="relative" zIndex={1}>
          <PersonAvatar size={48} sx={{ flexShrink: 0 }} />

          <Stack minWidth={0} flex={1}>
            <Typography id={TITLE_ID} fontWeight={700} fontSize={18} noWrap>
              {booking ? guestName : ' '}
            </Typography>
            {booking?.phoneNumber && (
              <Link
                href={`tel:${booking.phoneNumber.replace(/\s+/g, '')}`}
                underline="hover"
                noWrap
                sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
              >
                {booking.phoneNumber}
              </Link>
            )}
            {/* The account's address, or for a booking made by hand the one typed in; opens a mail. */}
            {booking?.email && (
              <Link
                href={`mailto:${booking.email}`}
                underline="hover"
                noWrap
                data-testid="booking-details-email"
                sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
              >
                {booking.email}
              </Link>
            )}
          </Stack>

          {booking && (
            <Chip
              icon={<StatusIcon sx={{ fontSize: '14px !important', color: 'inherit !important' }} />}
              label={status.label}
              size="small"
              sx={{
                flexShrink: 0,
                fontWeight: 600,
                fontSize: '11px',
                height: 24,
                color: '#fff',
                bgcolor: alpha(status.border, 0.22),
                border: `1px solid ${alpha(status.border, 0.5)}`,
              }}
            />
          )}
        </Stack>
      </Box>

      {!booking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {/* White body — when, and for what */}
          <Stack spacing={2} sx={{ px: 3, pt: 2.5, pb: canDecide ? 1 : 2.5 }}>
            <Stack
              direction="row"
              spacing={2}
              divider={<Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.08)', flexShrink: 0 }} />}
            >
              <InfoTile
                icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
                label="Dátum"
                value={getDisplayDate(date)}
                detail={date.format('dddd')}
              />
              <InfoTile
                icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                label="Időpont"
                value={`${booking.startTime} – ${booking.endTime}`}
                detail={durationMinutes > 0 ? `${durationMinutes} perc` : null}
              />
            </Stack>

            {booking.supply && <SupplyDetails supply={booking.supply} booking={booking} />}
          </Stack>

          {canDecide && (
            <Box sx={{ px: 3, pt: 1.5, pb: 2.5 }}>
              <BookingDecisionButtons
                onApprove={() => onDecision('APPROVE', booking.id)}
                onReject={() => onDecision('REJECT', booking.id)}
                disabled={loading}
              />
            </Box>
          )}

          {/* Footer — the destructive action kept apart from the decision, and absent while one is on offer */}
          <Stack
            direction="row"
            justifyContent={canDelete ? 'space-between' : 'flex-end'}
            alignItems="center"
            sx={{ px: 2, py: 1.25, borderTop: '1px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.015)' }}
          >
            {canDelete && (
              <Button
                color="error"
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
                onClick={deleteBooking}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px' }}
              >
                {isBlocked ? 'Letiltás feloldása' : 'Foglalás törlése'}
              </Button>
            )}
            <Button
              onClick={handleClose}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', color: 'text.secondary' }}
            >
              Bezárás
            </Button>
          </Stack>
        </>
      )}
    </Dialog>
  );
};

export default BookingDetailsModal;
